import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Users, UserPlus, UserMinus, X, AlertTriangle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  useListAccessGroups,
  useListUsers,
  useListGroupMembers,
  useAddGroupMembers,
  useRemoveGroupMembers,
} from '@/hooks/useIAM';
import { User, GroupMember } from '@/types/iam';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  userName,
  action,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  userName: string;
  action: 'add' | 'remove';
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsProcessing(true);

    const toastId = toast.loading(`${action === 'add' ? 'Adding' : 'Removing'} user "${userName}"...`);

    try {
      await onConfirm();

      toast.success(`User "${userName}" ${action === 'add' ? 'added to' : 'removed from'} group successfully`, { id: toastId });

      onClose();
    } catch (error) {
      console.error(`Error ${action === 'add' ? 'adding' : 'removing'} user:`, error);

      toast.error(`Error ${action === 'add' ? 'adding' : 'removing'} user "${userName}". Please try again.`, {
        id: toastId,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
    >
      <div className="rounded-lg shadow-lg max-w-md w-full bg-background">
        <div className="p-5 border-b border-border flex justify-between items-center">
          <div className="flex items-center text-secondary">
            <AlertTriangle className="mr-2" size={20} />
            <h3 className="text-lg font-medium">Confirm Action</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity text-muted-foreground"
            disabled={isProcessing}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <p className="mb-4 text-foreground">
            Are you sure you want to {action === 'add' ? 'add' : 'remove'}{' '}
            <span className="font-semibold">{userName}</span> {action === 'add' ? 'to' : 'from'} this group?
          </p>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted/50"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className={cn(
                "px-4 py-2 rounded-md transition-colors flex items-center text-primary-foreground",
                action === 'add' ? "bg-primary hover:opacity-90" : "bg-destructive hover:opacity-90"
              )}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {action === 'add' ? 'Adding...' : 'Removing...'}
                </>
              ) : (
                action === 'add' ? 'Add' : 'Remove'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GroupMembersManager = () => {
  const { accessGroups, fetchAccessGroups } = useListAccessGroups();
  const { users, fetchUsers } = useListUsers();
  const { members, fetchGroupMembers, loading: membersLoading } = useListGroupMembers();
  const { addGroupMembers } = useAddGroupMembers();
  const { removeGroupMembers } = useRemoveGroupMembers();

  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'add' | 'remove'>('add');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Initialize data
  useEffect(() => {
    fetchAccessGroups();
    fetchUsers();
  }, [fetchAccessGroups, fetchUsers]);

  // Fetch members when group is selected
  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupMembers(selectedGroupId);
    }
  }, [selectedGroupId, fetchGroupMembers]);

  const selectedGroup = accessGroups.find(group => group.id === selectedGroupId);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableUsers = filteredUsers.filter(user =>
    !members.some(member => member.id === user.id)
  );

  const handleAddUser = (user: User) => {
    setSelectedUser(user);
    setModalAction('add');
    setIsModalOpen(true);
  };

  const handleRemoveUser = (member: GroupMember) => {
    setSelectedUser(member as User);
    setModalAction('remove');
    setIsModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedUser || !selectedGroupId) return;

    try {
      if (modalAction === 'add') {
        await addGroupMembers(selectedGroupId, { members: [selectedUser.id] });
      } else {
        await removeGroupMembers(selectedGroupId, { members: [selectedUser.id] });
      }

      // Refresh members list
      await fetchGroupMembers(selectedGroupId);

      setIsModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  if (membersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          Group Members Management
        </h2>

        {/* Group Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-foreground">
            Select Access Group
          </label>
          <select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
          >
            <option value="">Select a group...</option>
            {accessGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} (Priority: {group.priority})
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedGroupId && selectedGroup && (
        <>
          {/* Group Info */}
          <div
            className="p-4 rounded-lg mb-6 bg-primary/10 border border-primary"
          >
            <h3 className="text-lg font-semibold mb-2 text-primary">
              {selectedGroup.name}
            </h3>
            <p className="text-sm mb-2 text-foreground">
              {selectedGroup.description || 'No description'}
            </p>
            <p className="text-sm text-muted-foreground">
              Priority: {selectedGroup.priority} | Roles: {selectedGroup.roles.length} | Members: {members.length}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Members */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center text-foreground">
                <Users className="mr-2" size={20} />
                Current Members ({members.length})
              </h3>

              <div
                className="rounded-lg shadow max-h-96 overflow-y-auto bg-background"
              >
                {members.length === 0 ? (
                  <div className="p-6 text-center">
                    <Users size={48} className="mx-auto mb-2 text-muted-foreground" />
                    <p className="text-muted-foreground">No members in this group</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {members.map((member) => (
                      <div key={member.id} className="p-4 flex justify-between items-center">
                        <div>
                          <div className="font-medium text-foreground">
                            {member.name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {member.email}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveUser(member)}
                          className="p-2 rounded-md transition-colors flex items-center bg-destructive text-destructive-foreground hover:opacity-80"
                        >
                          <UserMinus size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Available Users */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center text-foreground">
                <UserPlus className="mr-2" size={20} />
                Available Users ({availableUsers.length})
              </h3>

              {/* Search */}
              <div className="mb-4 relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
                />
              </div>

              <div
                className="rounded-lg shadow max-h-96 overflow-y-auto bg-background"
              >
                {availableUsers.length === 0 ? (
                  <div className="p-6 text-center">
                    <UserPlus size={48} className="mx-auto mb-2 text-muted-foreground" />
                    <p className="text-muted-foreground">
                      {searchTerm ? 'No users match your search' : 'No available users to add'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {availableUsers.map((user) => (
                      <div key={user.id} className="p-4 flex justify-between items-center">
                        <div>
                          <div className="font-medium text-foreground">
                            {user.name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {user.email}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddUser(user)}
                          className="p-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-80"
                        >
                          <UserPlus size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmAction}
        userName={selectedUser?.name || ''}
        action={modalAction}
      />
    </div>
  );
};

export default GroupMembersManager;
